import React, { useState, useEffect, useRef } from "react";
import { ReactComponent as ChevronDownIcon } from "../../assets/Icons/Chevron down.svg";
import { ReactComponent as ChevronUp } from "../../assets/Icons/ChevronUp.svg";
import { axiosPostApi, axiosGetApi } from "../../helpers/interceptorServices";
import DeleteConfirmationModal from "./DeleteConfirmationUser";
import AddIcon from "../../assets/Icons/add person icon.svg";
import CrossIcon from "../../assets/Icons/cross_svg.svg";
import CustomAlert from "../../helpers/CustomAlert";
import { apiList } from "../../helpers/apiList";
import confetti from "canvas-confetti";
import "../../css/CRUDModal.css";
import SearchSelect from "../common/SearchSelect";

// action "authorize": the same read-only view as "view", with the confirmation
// and an Authorize button in the footer (onAuthorize does the request)
const UserModal = ({ show, handleClose, editData, action, fetchUserData, onAuthorize, authorizing = false }) => {
  const loggedInUserName = sessionStorage.getItem("username");
  const profileRef = useRef(null);
  const roleRef = useRef(null);

  const [isModalOpen, setIsModalOpen] = useState({ show: false, type: "" });
  const [isLoadingUserName, setIsLoadingUserName] = useState(false);
  const [selectedUserRole, setSelectedUserRole] = useState("");
  const [profileOpen, setProfileOpen] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);

  const [formData, setFormData] = useState({
    username: loggedInUserName,
    name: "",
    mobileNo: "",
    emailId: "",
    userRole: [],
    userProfile: "",
    userCode: "",
    hubCode: "",
    selectedName: "",
    selectedHubName: "",
  });
  const [errors, setErrors] = useState({
    name: "",
    mobileNo: "",
    emailId: "",
    userRole: "",
    userProfile: "",
    userCode: "",
    hubCode: "",
    selectedName: "",
  });

  const toggleProfileDropdown = () => setProfileOpen(!profileOpen);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (action === "add") {
      console.log("🧩 editData from API:", editData);
      setFormData({
        username: loggedInUserName,
        name: "",
        mobileNo: "",
        emailId: "",
        userRole: [],
        userProfile: "",
        userCode: "",
        hubCode: "",
        selectedName: "",
        selectedHubName: "",
      });
      setSelectedUserRole("");
    } else if (
      action === "modify" ||
      action === "view" ||
      action === "authorize" ||
      action === "delete"
    ) {
      let updatedData = {
        ...editData,
        username: loggedInUserName,
        selectedName:
          editData.userProfile === "CUSTOMER"
            ? editData.customerName || editData.name || ""
            : "",
        userCode:
          editData.userProfile === "CUSTOMER"
            ? editData.userCode ||
              editData.customerCode ||
              editData.customerId ||
              ""
            : "",
        selectedHubName:
          editData.userProfile === "HUB"
            ? editData.hubName || editData.hub_name || editData.name || ""
            : "",
        hubCode:
          editData.userProfile === "HUB"
            ? editData.userCode ||
              editData.hubCode ||
              editData.hub_code ||
              editData.hubId ||
              editData.hub_id ||
              ""
            : "",
      };

      setFormData(updatedData);
      setSelectedUserRole(editData.roleName || "");
    }
  }, [action, editData, loggedInUserName]);

  const handleClickOutside = (event) => {
    if (roleRef.current && !roleRef.current.contains(event.target)) {
      setRoleOpen(false);
    }
    if (profileRef.current && !profileRef.current.contains(event.target)) {
      setProfileOpen(false);
    }
  };

  const handleProfileChange = (event) => {
    const selectedProfile = event.target.value;
    toggleProfileDropdown();

    // Set userRole based on selected profile
    let userRoleValue = "";
    if (selectedProfile === "CUSTOMER") {
      userRoleValue = "CUST";
    }

    setFormData({
      ...formData,
      userProfile: selectedProfile,
      userRole: selectedProfile === "CUSTOMER" ? userRoleValue : [],
    });

    // Clear the profile error immediately upon selection
    setErrors((prevErrors) => ({
      ...prevErrors,
      userProfile: "",
    }));

    // Set selectedUserRole for CUSTOMER profile
    if (selectedProfile === "CUSTOMER") {
      setSelectedUserRole("CUST");
    } else {
      setSelectedUserRole(""); // Clear selected role for other profiles
    }

    setRoleOpen(false); // Close the dropdown if open

    let apiUrl = "";
    if (selectedProfile === "CUSTOMER") {
      apiUrl = apiList.ALL_ACTIVE_CUSTOMER_DATA;
    } else if (selectedProfile === "HUB") {
      apiUrl = apiList.ALL_ACTIVE_HUB_DATA;
    } else if (selectedProfile === "Franchise") {
      apiUrl = `${apiList.ALL_ACTIVE_FRANCHISE_DATA}?username=${loggedInUserName}`;
    }

    setTableData([]);
    if (apiUrl) {
      const requestBody = {
        username: loggedInUserName,
        status: "",
      };

      setTableLoading(true);
      axiosGetApi(apiUrl, requestBody)
        .then((response) => {
          if (response?.status === 200 && response?.data?.data) {
            setTableData(response?.data?.data);
          }
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
        })
        .finally(() => setTableLoading(false));
    }
  };

  const isAuthorizeMode = action === "authorize";
  const isViewMode = action === "view" || isAuthorizeMode;
  const isDeleteMode = action === "delete";
  const isEditMode = action === "modify";
  // Adding a user picks the LMBP / franchise / hub inline; other modes show it read-only
  const isPickingInline = !isViewMode && !isDeleteMode && !isEditMode;

  const isActive = (row) =>
    !row?.authStatus || String(row.authStatus).toUpperCase() === "ACTIVE" || String(row.authStatus) === "1";

  const pickerOptions = (tableData || []).map((row) => {
    if (formData?.userProfile === "HUB") {
      return {
        value: row.hubCode,
        label: row.hubName,
        meta: [row.city, row.contactPersonName].filter(Boolean).join(" · "),
        row,
      };
    }
    if (formData?.userProfile === "Franchise") {
      return {
        value: row.franchiseCode,
        label: row.franchiseName,
        meta: [row.city, row.primaryContactName, row.primaryContactMobile].filter(Boolean).join(" · "),
        disabled: !isActive(row),
        disabledReason: "Inactive franchise: activate it to add users",
        row,
      };
    }
    return {
      value: row.customerCode,
      label: row.name,
      meta: [row.city, row.firstContactPersonName, row.firstContactPersonMobileNo].filter(Boolean).join(" · "),
      disabled: !isActive(row),
      disabledReason: "Inactive LMBP: activate it to add users",
      row,
    };
  });

  const handlePick = ({ value, label, row }) => {
    if (formData?.userProfile === "HUB") {
      setFormData((prev) => ({ ...prev, hubCode: value, selectedHubName: label }));
      setErrors((prev) => ({ ...prev, hubCode: "" }));
      return;
    }
    // Pre-fill the user's details from the selected LMBP / franchise contact;
    // keep anything already typed if the record lacks that field.
    const contactName =
      formData?.userProfile === "Franchise" ? row?.primaryContactName : row?.firstContactPersonName || row?.name;
    const contactMobile =
      formData?.userProfile === "Franchise" ? row?.primaryContactMobile : row?.firstContactPersonMobileNo;
    setFormData((prev) => ({
      ...prev,
      userCode: value,
      selectedName: label,
      name: contactName || prev?.name,
      mobileNo: contactMobile || prev?.mobileNo,
      emailId: row?.emailId || prev?.emailId,
    }));
    setErrors((prev) => ({ ...prev, userCode: "", name: "", mobileNo: "", emailId: "" }));
  };

  const openModal = (type) => {
    setIsModalOpen({ show: true, type });
  };

  const closeModal = () => {
    setIsModalOpen({ show: false, type: "" });
  };

  const handleRole = () => {
    if (!formData.userProfile) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        userProfile: "Please select a profile before selecting a role",
      }));
      return; // Exit the function to prevent further execution
    }
    setRoleOpen(!roleOpen);
    if (!formData.userProfile) return;
    setIsLoadingUserName(true);
    const requestBody = {
      username: loggedInUserName,
      userProfile: formData?.userProfile,
      status: "1",
    };

    axiosPostApi(apiList.MENU_USERROLE_DATA, requestBody)
      .then((response) => {
        if (
          response?.status === 200 &&
          response?.data?.data?.userRolesWithNames
        ) {
          const roles = response?.data?.data?.userRolesWithNames.map(
            (role) => ({
              userId: role.userRole,
              name: role.roleName,
            }),
          );
          setFormData((prevFormData) => ({
            ...prevFormData,
            userRole: roles,
          }));
          setIsLoadingUserName(false);
        }
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setIsLoadingUserName(false);
      });
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    let updatedValue = value;
    if (id === "name") {
      updatedValue = value.toUpperCase();
    } else if (id === "emailId") {
      updatedValue = value.toLowerCase();
    } else if (id === "mobileNo") {
      updatedValue = value.replace(/[^0-9]/g, "");
    } else {
      updatedValue = value.trimStart(); // optional: prevent leading spaces
    }
    setFormData({ ...formData, [id]: updatedValue });
  };

  const validate = () => {
    const newErrors = {};

    formData.name = formData.name.trim();
    if (!formData.name) {
      newErrors.name = "Name is required";
    } else if (!/^[A-Za-z]+(?: [A-Za-z]+)*$/.test(formData.name)) {
      newErrors.name = "Name must contain only letters and spaces";
    } else if (formData.name.length < 2 || formData.name.length > 50) {
      newErrors.name = "Name must be between 2 and 50 characters long";
    } else {
      const nameWithoutSpaces = formData.name.replace(/\s+/g, "").toLowerCase();
      const firstChar = nameWithoutSpaces[0];
      const allSame = nameWithoutSpaces
        .split("")
        .every((char) => char === firstChar);

      if (allSame) {
        newErrors.name = "Name cannot consist of the same repeated letter";
      }
    }

    // Contact validation
    if (!formData.mobileNo) {
      newErrors.mobileNo = "Contact number is required";
    } else if (!/^[6789]\d{9}$/.test(formData.mobileNo)) {
      newErrors.mobileNo =
        "Mobile number must be exactly 10 digits and start with 6, 7, 8, or 9";
    } else {
      const allSameDigit = formData.mobileNo
        .split("")
        .every((digit) => digit === formData.mobileNo[0]);
      if (allSameDigit) {
        newErrors.mobileNo = "Mobile number cannot have all digits the same";
      }
    }

    // Role validation
    if (!formData.userRole) {
      newErrors.userRole = "Role is required";
    }
    // Profile validation
    if (!formData.userProfile) {
      newErrors.userProfile = "Profile is required";
    }

    if (formData.userProfile === "CUSTOMER") {
      if (!formData.userCode) {
        newErrors.userCode = "LMBP Id is required";
      }
    }

    if (formData.userProfile === "HUB") {
      if (!formData.hubCode) {
        newErrors.hubCode = "Hub Id is required";
      }
    }

    // Email validation (required and valid email)
    if (!formData.emailId) {
      newErrors.emailId = "Email is required";
    } else if (
      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.(com|in|pe|co\.[a-zA-Z]{2})$/.test(
        formData.emailId,
      ) ||
      /\.com\./.test(formData.emailId) ||
      /\.co\.[a-zA-Z]{2}\./.test(formData.emailId)
    ) {
      newErrors.emailId =
        "Enter valid email ending with .com, .co.xx, .pe or .in";
    }

    return newErrors;
  };

  const handleBlur = (e) => {
    const { id, value } = e.target;
    const fieldErrors = {};
    const trimmedValue = value.trim();

    switch (id) {
      case "name":
        if (!trimmedValue) {
          fieldErrors.name = "Name is required";
        } else if (!/^[A-Za-z]+(?: [A-Za-z]+)*$/.test(trimmedValue)) {
          fieldErrors.name = "Name must contain only letters and spaces";
        } else if (trimmedValue.length < 2 || trimmedValue.length > 50) {
          fieldErrors.name = "Name must be between 2 and 50 characters long";
        } else {
          const nameWithoutSpaces = trimmedValue
            .replace(/\s+/g, "")
            .toLowerCase();
          const firstChar = nameWithoutSpaces[0];
          const allSame = nameWithoutSpaces
            .split("")
            .every((char) => char === firstChar);

          if (allSame) {
            fieldErrors.name =
              "Name cannot consist of the same repeated letter";
          } else {
            fieldErrors.name = "";
          }
        }
        break;

      case "mobileNo":
        if (!trimmedValue) {
          fieldErrors.mobileNo = "Contact number is required";
        } else if (!/^[6789]\d{9}$/.test(trimmedValue)) {
          fieldErrors.mobileNo =
            "Mobile number must be exactly 10 digits and start with 6, 7, 8, or 9";
        } else {
          const allSameDigit = trimmedValue
            .split("")
            .every((digit) => digit === trimmedValue[0]);
          if (allSameDigit) {
            fieldErrors.mobileNo =
              "Mobile number cannot have all digits the same";
          } else {
            fieldErrors.mobileNo = "";
          }
        }
        break;

      case "emailId":
        if (!trimmedValue) {
          fieldErrors.emailId = "Email is required";
        } else if (
          !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.(com|in|pe|co\.[a-zA-Z]{2})$/.test(
            trimmedValue,
          ) ||
          /\.in\./.test(trimmedValue) ||
          /\.co\.[a-zA-Z]{2}\./.test(trimmedValue)
        ) {
          fieldErrors.emailId =
            "Enter valid email ending with .com, .co.xx, .pe or .in";
        } else {
          fieldErrors.emailId = "";
        }
        break;

      case "userCode":
        if (!trimmedValue) {
          fieldErrors.userCode = "LMBP Id is required";
        } else {
          fieldErrors.userCode = "";
        }
        break;

      case "hubCode":
        if (!trimmedValue) {
          fieldErrors.hubCode = "Hub Id is required";
        } else {
          fieldErrors.hubCode = "";
        }
        break;

      default:
        break;
    }

    setErrors((prev) => ({
      ...prev,
      ...fieldErrors,
    }));
  };

  const handleAddUser = (e) => {
    // e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      let apiUrl = "";
      let successMessage = "";

      if (action === "add") {
        apiUrl = apiList.CREATE_USER_DATA; // Add
        successMessage = "User added successfully";
      } else if (action === "modify") {
        apiUrl = apiList.MODIFY_USER_DATA; // Modify
        successMessage = "User details modified successfully";
      }
      const requestData = {
        ...formData,
        userRole: selectedUserRole,
      };

      const requestHubData = {
        username: formData?.username,
        name: formData?.name,
        mobileNo: formData?.mobileNo,
        emailId: formData?.emailId,
        userRole: selectedUserRole,
        userProfile: formData?.userProfile,
        userCode: formData?.hubCode,
        selectedName: formData?.selectedHubName,
      };

      const requestFranchiseData = {
        username: formData?.username,
        name: formData?.name,
        mobileNo: formData?.mobileNo,
        emailId: formData?.emailId,
        userRole: "FRANAD",
        userProfile: "FRANCHISE",
        userCode: formData?.userCode,
        selectedName: formData?.selectedName,
      };

      const payload =
        formData?.userProfile === "HUB"
          ? requestHubData
          : formData?.userProfile === "Franchise"
          ? requestFranchiseData
          : requestData;

      axiosPostApi(apiUrl, payload)
        .then((response) => {
          if (response?.status === 200 && response?.data?.status === true) {
            CustomAlert({
              title: "Success",
              text: response?.data?.message || "User Added Successfully",
              icon: "success",
              confirmButtonText: "Ok",
            });
            const canvas = document.createElement("canvas");
            canvas.style.position = "fixed";
            canvas.style.top = "0";
            canvas.style.left = "0";
            canvas.style.width = "100%";
            canvas.style.height = "100%";
            canvas.style.zIndex = "3000";
            document.body.appendChild(canvas);

            const confettiInstance = confetti.create(canvas, {
              resize: true,
              useWorker: true,
            });

            confettiInstance({
              particleCount: 250,
              spread: 100,
              origin: { y: 0.6 },
            });

            setTimeout(() => {
              document.body.removeChild(canvas);
            }, 3000);
            handleClose();
            fetchUserData(1);
          } else {
            CustomAlert({
              title:
                action === "add"
                  ? "Failed to Add User"
                  : "Failed to Modify User",
              text:
                response.data.message ||
                "There was an issue processing the request. Please try again.",
              icon: "error",
              confirmButtonText: "Retry",
            });
          }
        })
        .catch((error) => {
          console.error("Error processing request:", error);
          CustomAlert({
            title: "Error",
            text:
              action === "add"
                ? "An error occurred while adding the user. Please try again later."
                : "An error occurred while modifying the user. Please try again later.",
            icon: "error",
            confirmButtonText: "Retry",
          });
        });
    }
  };

  const handleBack = () => {
    handleClose();
    setErrors({});
  };

  const handleRoleSelection = (event) => {
    setSelectedUserRole(event.target.value);
  };

  return (
    <>
      <div
        className={`modal-lg modal fade ${show ? "show" : ""}`}
        tabIndex="-1"
        role="dialog"
        style={{
          display: show ? "flex" : "none",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <div className="modal-dialog" role="document">
          <div className="modal-content w-[1070px] rounded-3xl shadow-lg">
            {/* Header */}
            <div className="modal-header justify-between flex border-b-0 px-6 pt-5 pb-0">
              <div className="flex items-center gap-4">
                <img src={AddIcon} alt="filter icon" className="w-10 h-10" />
                <h5 className="modal-title font-semibold text-lg text-gray-700">
                  {action.charAt(0).toUpperCase() + action.slice(1)} User
                  Details
                </h5>
              </div>
              <img
                src={CrossIcon}
                alt="close"
                className="w-[30px] h-[30px] cursor-pointer"
                onClick={handleClose}
              />
            </div>

            {/* Body */}
            <form>
              <div className="modal-body px-6 pb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-3 gap-y-5">
                  {/* Name */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <i className="fa fa-user absolute left-3 top-3 text-[#FF702B] text-sm"></i>
                      <input
                        type="text"
                        id="name"
                        placeholder="Name"
                        value={formData?.name || ""}
                        disabled={isViewMode || isDeleteMode}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        className={`w-full h-[42px] rounded-lg border border-gray-300 pl-9 pr-3 text-sm placeholder-gray-400
                        focus:outline-none focus:ring-0 focus:border-[#FF702B]
                        ${
                          isViewMode || isDeleteMode
                            ? "bg-gray-100 cursor-not-allowed"
                            : "bg-white"
                        }`}
                      />
                    </div>
                    {!(isViewMode || isDeleteMode) && errors.name && (
                      <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                    )}
                  </div>

                  {/* Contact No */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                      Contact No <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <i className="fa fa-phone absolute left-3 top-3 text-[#FF702B] text-sm"></i>
                      <input
                        type="text"
                        id="mobileNo"
                        placeholder="Contact No"
                        value={formData?.mobileNo || ""}
                        maxLength={10}
                        disabled={isViewMode || isDeleteMode}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d{0,10}$/.test(value)) handleInputChange(e);
                        }}
                        onBlur={handleBlur}
                        className={`w-full h-[42px] rounded-lg border border-gray-300 pl-9 pr-3 text-sm placeholder-gray-400
                        focus:outline-none focus:ring-0 focus:border-[#FF702B]
                        ${
                          isViewMode || isDeleteMode
                            ? "bg-gray-100 cursor-not-allowed"
                            : "bg-white"
                        }`}
                      />
                    </div>
                    {!(isViewMode || isDeleteMode) && errors.mobileNo && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.mobileNo}
                      </p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                      User Email ID <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <i className="fa fa-envelope absolute left-3 top-3 text-[#FF702B] text-sm"></i>
                      <input
                        type="email"
                        id="emailId"
                        placeholder="Email ID"
                        value={formData?.emailId || ""}
                        disabled={isViewMode || isDeleteMode}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        className={`w-full h-[42px] rounded-lg border border-gray-300 pl-9 pr-3 text-sm placeholder-gray-400
                        focus:outline-none focus:ring-0 focus:border-[#FF702B]
                        ${
                          isViewMode || isDeleteMode
                            ? "bg-gray-100 cursor-not-allowed"
                            : "bg-white"
                        }`}
                      />
                    </div>
                    {!(isViewMode || isDeleteMode) && errors.emailId && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.emailId}
                      </p>
                    )}
                  </div>

                  {/* Profile */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                      User Profile <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <i className="fa fa-user-tie absolute left-3 top-3 text-[#FF702B] text-sm"></i>
                      <select
                        id="userProfile"
                        value={formData?.userProfile || ""}
                        disabled={isViewMode || isDeleteMode || isEditMode}
                        onChange={handleProfileChange}
                        onBlur={handleBlur}
                        className={`w-full h-[42px] rounded-lg border border-gray-300 pl-9 pr-3 text-sm text-gray-600
                        focus:outline-none focus:ring-0 focus:border-[#FF702B]
                        ${
                          isViewMode || isDeleteMode
                            ? "bg-gray-100 cursor-not-allowed"
                            : "bg-white"
                        }`}
                      >
                        <option value="">Select Profile</option>
                        <option value="OPERATION">Operation</option>
                        <option value="Franchise">Franchise</option>
                        <option value="CUSTOMER">LMBP</option>
                        <option value="HUB">Hub</option>
                      </select>
                    </div>
                    {!(isViewMode || isDeleteMode || isEditMode) &&
                      errors.userProfile && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.userProfile}
                        </p>
                      )}
                  </div>

                  {/* Role (if not CUSTOMER OR Franchise) */}
                  {formData?.userProfile !== "CUSTOMER" &&
                    formData?.userProfile !== "Franchise" && (
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">
                          Role <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <i className="fa fa-briefcase absolute left-3 top-3 text-[#FF702B] text-sm"></i>
                          {isViewMode || isDeleteMode ? (
                            <input
                              type="text"
                              value={formData?.roleName || "N/A"}
                              readOnly
                              className="w-full h-[42px] rounded-lg border border-gray-300 bg-gray-100 pl-9 pr-3 text-sm text-gray-500 cursor-not-allowed"
                            />
                          ) : (
                            <select
                              id="userRole"
                              value={selectedUserRole}
                              onClick={handleRole}
                              onChange={handleRoleSelection}
                              onBlur={handleBlur}
                              disabled={isEditMode}
                              className={`w-full h-[42px] rounded-lg border border-gray-300 pl-9 pr-3 text-sm text-gray-600
                            focus:outline-none focus:ring-0 focus:border-[#FF702B]
                            ${
                              isEditMode
                                ? "bg-gray-100 cursor-not-allowed"
                                : "bg-white"
                            }`}
                            >
                              <option value="">
                                {isLoadingUserName
                                  ? "Loading..."
                                  : isEditMode
                                  ? formData?.roleName || "Select Role"
                                  : "Select Role"}
                              </option>
                              {!isLoadingUserName &&
                                Array.isArray(formData.userRole) &&
                                formData.userRole.map((user) => (
                                  <option key={user.userId} value={user.userId}>
                                    {user.name}
                                  </option>
                                ))}
                            </select>
                          )}
                        </div>
                        {!(isViewMode || isDeleteMode || isEditMode) &&
                          errors.userRole && (
                            <p className="text-red-500 text-xs mt-1">
                              {errors.userRole}
                            </p>
                          )}
                      </div>
                    )}

                  {/* LMBP ID (CUSTOMER || Franchise) */}
                  {(formData?.userProfile === "CUSTOMER" ||
                    formData?.userProfile === "Franchise") && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        {formData?.userProfile === "CUSTOMER"
                          ? "LMBP ID"
                          : "Franchise ID"}{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      {isPickingInline ? (
                        <SearchSelect
                          id="userCode"
                          options={pickerOptions}
                          value={formData?.userCode}
                          onSelect={handlePick}
                          loading={tableLoading}
                          error={errors.userCode}
                          placeholder={
                            formData?.userProfile === "CUSTOMER"
                              ? "Type LMBP name, ID, city or mobile"
                              : "Type franchise name, ID, city or mobile"
                          }
                          emptyText={
                            formData?.userProfile === "CUSTOMER" ? "No active LMBP matches" : "No active franchise matches"
                          }
                        />
                      ) : (
                      <div className="relative">
                        <i className="fa fa-building absolute left-3 top-3 text-[#FF702B] text-sm"></i>
                        <input
                          type="text"
                          id="userCode"
                          placeholder={
                            formData?.userProfile === "CUSTOMER"
                              ? "Select LMBP ID"
                              : "Select Franchise ID"
                          }
                          value={formData?.userCode || ""}
                          readOnly
                          disabled
                          className="w-full h-[42px] rounded-lg border border-gray-300 pl-9 pr-3 text-sm placeholder-gray-400 bg-gray-100 cursor-not-allowed"
                          autoComplete="off"
                        />
                      </div>
                      )}
                      {errors.userCode && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.userCode}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Hub ID (HUB) */}
                  {formData?.userProfile === "HUB" && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        Hub ID <span className="text-red-500">*</span>
                      </label>
                      {isPickingInline ? (
                        <SearchSelect
                          id="hubCode"
                          icon="fa fa-warehouse"
                          options={pickerOptions}
                          value={formData?.hubCode}
                          onSelect={handlePick}
                          loading={tableLoading}
                          error={errors.hubCode}
                          placeholder="Type hub name, ID or city"
                          emptyText="No active hub matches"
                        />
                      ) : (
                      <div className="relative">
                        <i className="fa fa-warehouse absolute left-3 top-3 text-[#FF702B] text-sm"></i>
                        <input
                          type="text"
                          id="hubCode"
                          value={formData?.hubCode || ""}
                          readOnly
                          disabled
                          className="w-full h-[42px] rounded-lg border border-gray-300 pl-9 pr-3 text-sm placeholder-gray-400 bg-gray-100 cursor-not-allowed"
                        />
                      </div>
                      )}
                      {errors.hubCode && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.hubCode}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Selected Names (read-only fields); the inline picker already shows the name */}
                  {!isPickingInline &&
                    (formData?.userProfile === "CUSTOMER" ||
                    formData?.userProfile === "Franchise") && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        {formData?.userProfile === "CUSTOMER"
                          ? "LMBP Name"
                          : "Franchise Name"}
                      </label>
                      <div className="relative">
                        <i className="fa fa-building absolute left-3 top-3 text-[#FF702B] text-sm"></i>
                        <input
                          type="text"
                          id="selectedName"
                          placeholder={
                            formData?.userProfile === "CUSTOMER"
                              ? "Selected LMBP Name"
                              : "Selected Franchise Name"
                          }
                          value={formData?.selectedName || ""}
                          disabled
                          readOnly
                          className="w-full h-[42px] rounded-lg border border-gray-300 bg-gray-100 pl-9 pr-3 text-sm text-gray-500 cursor-not-allowed"
                        />
                      </div>
                    </div>
                  )}

                  {!isPickingInline && formData?.userProfile === "HUB" && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        Hub Name
                      </label>
                      <div className="relative">
                        <i className="fa fa-warehouse absolute left-3 top-3 text-[#FF702B] text-sm"></i>
                        <input
                          type="text"
                          id="selectedHubName"
                          placeholder="Selected Hub Name"
                          value={formData?.selectedHubName || ""}
                          disabled
                          readOnly
                          className="w-full h-[42px] rounded-lg border border-gray-300 bg-gray-100 pl-9 pr-3 text-sm text-gray-500 cursor-not-allowed"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer Buttons */}
              {isAuthorizeMode && (
                <p className="text-center text-gray-700 font-medium mb-0 px-6">
                  Are you sure? Do you want to authorize this{" "}
                  {editData?.userProfileDisplay || editData?.userProfile || ""} user
                  {editData?.userId ? ` (${editData.userId})` : ""}?
                </p>
              )}
              <div className="scoped-modal modal-footer justify-center border-t-0">
                <button
                  type="button"
                  className="btn btn-back w-[133px] !rounded-lg"
                  onClick={handleBack}
                >
                  Back
                </button>
                {!isViewMode && !isDeleteMode && (
                  <div
                    // type="submit"
                    className="btn btn-submit w-[133px] !rounded-lg"
                    onClick={() => handleAddUser()}
                  >
                    {isEditMode ? "Update" : "Submit"}
                  </div>
                )}
                {isAuthorizeMode && (
                  <button
                    type="button"
                    className="btn btn-submit w-[133px] !rounded-lg"
                    disabled={authorizing}
                    onClick={() => onAuthorize && onAuthorize(editData)}
                  >
                    {authorizing ? "Authorizing…" : "Authorize"}
                  </button>
                )}
                {isDeleteMode && (
                  <button
                    type="button"
                    className="btn btn-danger w-[133px] !rounded-lg"
                    onClick={() => openModal("delete")}
                  >
                    Delete
                  </button>
                )}
              </div>
            </form>
            <DeleteConfirmationModal
              isOpen={isModalOpen.show}
              onClose={closeModal}
              fetchUserData={fetchUserData}
              editData={editData}
              handleClose={handleBack}
              mode={isModalOpen.type}
              tableDataUser={tableData}
              setFormData={setFormData}
              formData={formData}
            />
          </div>
        </div>
      </div>

      {/* Backdrop */}
      {(show || isModalOpen.show) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </>
  );
};

export default UserModal;
